import { useState, useRef, useEffect } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { BulkImportAPI, TenantsAPI, InstitutionsAPI, CampusesAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, FileText, Loader2, CheckCircle2, XCircle, Eye, KeyRound } from "lucide-react";

const RESOURCES = [
  { value: "institutions", label: "Institutions" },
  { value: "campuses", label: "Campuses" },
  { value: "academic-years", label: "Academic years" },
  { value: "classes", label: "Classes" },
  { value: "sections", label: "Sections" },
  { value: "subjects", label: "Subjects" },
  { value: "students", label: "Students" },
  { value: "parents", label: "Parents" },
  { value: "staff", label: "Staff" },
];

// Which resources accept a pinned institution / campus from the dropdowns.
// Classes, staff, and students already existed. Sections was missing — without
// campus pinning the class-name lookup can silently pick the wrong campus when
// both campuses share a class name ("Class 5" exists in both).
const CAMPUS_PIN_RESOURCES = new Set(["classes", "sections", "staff", "students"]);
const INSTITUTION_PIN_RESOURCES = new Set([
  "campuses", "academic-years", "classes", "sections", "subjects",
  "students", "parents", "staff",
]);

// Known columns per resource — mirrors what the backend importer will actually
// read. Unknown headers still upload fine (they're just ignored), but flagging
// them in the preview catches typos like `E-mail` or `admission no` fast.
// Extra aliases (first_name/last_name/aadhar_no/mobile_number/etc.) are
// included because the parser accepts them even when the sample uses the
// canonical form.
const KNOWN_COLUMNS = {
  institutions: new Set(["name", "type", "board", "code"]),
  campuses: new Set(["name", "institution_name", "institution_id", "address", "city", "state", "country", "code"]),
  "academic-years": new Set(["name", "institution_name", "institution_id", "start_date", "end_date", "is_current"]),
  classes: new Set(["name", "institution_name", "institution_id", "academic_year_name", "academic_year_id", "campus_id", "campus_name", "code", "order"]),
  sections: new Set(["name", "class_name", "class_id", "institution_name", "institution_id", "academic_year_name", "academic_year_id", "capacity"]),
  subjects: new Set(["name", "class_name", "class_id", "institution_name", "institution_id", "academic_year_name", "academic_year_id", "code"]),
  students: new Set([
    "sl_no", "name", "full_name", "first_name", "last_name", "admission_no", "adm_no",
    "gender", "dob", "blood_group",
    "institution_name", "institution_id", "class_name", "class_id",
    "academic_year_name", "academic_year_id", "section_name", "section_id",
    "contact_phone", "mobile_number", "contact_email", "email",
    "address", "father_name", "mother_name", "parent_email",
    "aadhaar_no", "aadhar_no", "apaar_id", "photo_url",
  ]),
  parents: new Set([
    "sl_no", "name", "full_name", "first_name", "last_name", "email", "phone",
    "occupation", "address", "student_admission_no", "relation", "password",
  ]),
  staff: new Set([
    "sl_no", "name", "full_name", "first_name", "last_name", "email",
    "password", "roles", "institution_name", "institution_id",
    "gender", "branch", "blood_group",
    "contact_no", "mobile_number", "phone", "address", "dob",
    "father_name", "mother_name",
    "date_of_joining", "hire_date", "designation", "department",
    "qualification", "experience",
    "aadhaar_no", "aadhar_no", "pan_no",
    "bank_account_no", "bank_account_details", "ifsc_code", "bank_name",
    "employee_id", "photo_url",
  ]),
};


/**
 * Normalise a CSV header the same way the backend parser does — lower, spaces
 * and dots and dashes → underscores. Keeps the client hint in sync with the
 * server's tolerance so we don't false-flag a header that would actually work.
 */
function normalizeHeader(h) {
  return String(h || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/\./g, "").replace(/-/g, "_");
}


/**
 * Minimal CSV parser for the preview panel.
 * - Handles quoted values with embedded commas, "" for escaped quotes,
 *   and quoted-newlines (a value that spans multiple lines).
 * - Returns { headers, rows: string[][], totalRows: number } where totalRows
 *   is `null` when we truncated after N rows (i.e. there may be more).
 */
function parseCsvChunk(text, maxRows = 5) {
  const out = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  const push = () => { row.push(field); field = ""; };
  const flush = () => { push(); out.push(row); row = []; };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { push(); }
      else if (ch === '\n') { flush(); if (out.length > maxRows) break; }
      else if (ch === '\r') { /* skip */ }
      else { field += ch; }
    }
  }
  if (field !== "" || row.length) flush();
  const [headers = [], ...data] = out;
  return {
    headers,
    rows: data.slice(0, maxRows),
    truncated: data.length > maxRows,
  };
}

export default function BulkImport() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const fileRef = useRef(null);
  const [searchParams] = useSearchParams();
  const initialResource = RESOURCES.some((r) => r.value === searchParams.get("resource"))
    ? searchParams.get("resource")
    : "institutions";
  const [resource, setResource] = useState(initialResource);
  const [tenantId, setTenantId] = useState(isSuper ? "" : user?.tenant_id || "");
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [preview, setPreview] = useState(null); // { headers, rows, totalRows }

  // Client-side CSV preview: parse the first ~5 data rows so the admin spots
  // column-mapping mistakes BEFORE running dry-run. Pure browser — no upload
  // happens here. Handles quoted values with embedded commas/newlines.
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        // Only read up to ~64KB — first 5 rows never need more than that.
        const chunk = await file.slice(0, 64 * 1024).text();
        const parsed = parseCsvChunk(chunk, 5);
        if (!cancelled) setPreview(parsed);
      } catch (err) {
        if (!cancelled) setPreview({ headers: [], rows: [], error: "Could not parse CSV" });
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  const showInstitution = INSTITUTION_PIN_RESOURCES.has(resource);
  const showCampus = CAMPUS_PIN_RESOURCES.has(resource);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  // Derive effective tenant: explicit selection wins, else first available.
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId || "self"],
    queryFn: () => InstitutionsAPI.list(isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined),
    enabled: showInstitution && (!isSuper || !!effectiveTenantId),
  });

  // Only honour institutionId if it still belongs to the current tenant's list.
  const effectiveInstitutionId =
    institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "";

  const campusesQuery = useQuery({
    queryKey: ["campuses", effectiveTenantId || "self", effectiveInstitutionId],
    queryFn: () => CampusesAPI.list({
      institution_id: effectiveInstitutionId,
      ...(isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : {}),
    }),
    enabled: showCampus && !!effectiveInstitutionId,
  });

  const effectiveCampusId =
    campusesQuery.data?.some((c) => c.id === campusId) ? campusId : "";

  const sampleQuery = useQuery({
    queryKey: ["bulk-sample", resource],
    queryFn: () => BulkImportAPI.sample(resource),
  });
  const sample = sampleQuery.data?.csv || "";

  const downloadSample = () => {
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${resource}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setBusy(true);
    setSummary(null);
    try {
      const out = await BulkImportAPI.upload(resource, file, {
        tenantId: isSuper ? effectiveTenantId : null,
        institutionId: showInstitution ? effectiveInstitutionId || null : null,
        campusId: showCampus ? effectiveCampusId || null : null,
        dryRun,
      });
      setSummary(out);
      if (out.dry_run) {
        toast.success(`Dry run: ${out.inserted}/${out.total} rows would import cleanly`);
      } else {
        toast.success(`${out.inserted}/${out.total} rows imported`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // For staff imports, offer a download of email/name/initial_password so
  // admins can hand teachers their credentials on Day 1.
  const downloadCredentials = () => {
    if (!summary) return;
    const okRows = summary.results.filter((r) => r.ok && r.data?.initial_password);
    if (!okRows.length) {
      toast.error("No credentials to download");
      return;
    }
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = ["email,name,initial_password,auto_generated"];
    for (const r of okRows) {
      lines.push([
        escape(r.data.email),
        escape(r.data.name || ""),
        escape(r.data.initial_password),
        r.data.auto_generated ? "true" : "false",
      ].join(","));
    }
    const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${summary.resource}-credentials.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${okRows.length} credentials`);
  };

  const hasCredentials = !!summary?.results?.some((r) => r.ok && r.data?.initial_password);

  return (
    <AppShell>
      <div className="p-8 max-w-5xl" data-testid="bulk-import-page">
        <div className="mb-8">
<h1 className="font-display font-extrabold tracking-tight text-4xl">Bulk import</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            CSV your way through onboarding. Upload a file, get a row-by-row
            report. Partial imports are allowed — errors do not abort the batch.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 border border-border rounded-md bg-card p-6 space-y-5" data-testid="bulk-import-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Resource</Label>
                <Select value={resource} onValueChange={setResource}>
                  <SelectTrigger data-testid="bulk-resource-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESOURCES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isSuper && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Tenant</Label>
                  <Select value={effectiveTenantId} onValueChange={setTenantId}>
                    <SelectTrigger data-testid="bulk-tenant-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showInstitution && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Institution</Label>
                  <Select value={effectiveInstitutionId} onValueChange={setInstitutionId}>
                    <SelectTrigger data-testid="bulk-institution-select">
                      <SelectValue placeholder={institutionsQuery.data?.length ? "Select institution (optional)" : "No institutions"} />
                    </SelectTrigger>
                    <SelectContent>
                      {institutionsQuery.data?.map((i) => (
                        <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Applied to every row when CSV omits institution_id / institution_name.</p>
                </div>
              )}
              {showCampus && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Campus</Label>
                  <Select value={effectiveCampusId} onValueChange={setCampusId} disabled={!effectiveInstitutionId}>
                    <SelectTrigger data-testid="bulk-campus-select">
                      <SelectValue placeholder={!effectiveInstitutionId ? "Pick an institution first" : (campusesQuery.data?.length ? "Select campus (optional)" : "No campuses")} />
                    </SelectTrigger>
                    <SelectContent>
                      {campusesQuery.data?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Applied to every class row when CSV omits campus_id.</p>
                </div>
              )}
            </div>

            <div
              className="border-2 border-dashed border-border rounded-md p-8 text-center hover:bg-secondary/30 transition cursor-pointer"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-file-dropzone"
            >
              <Upload className="h-7 w-7 mx-auto text-muted-foreground/60 mb-2" />
              {file ? (
                <div>
                  <div className="font-medium text-sm">{file.name}</div>
                  <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              ) : (
                <>
                  <div className="text-sm font-medium">Click to choose a CSV</div>
                  <div className="text-xs text-muted-foreground mt-1">UTF-8, comma-separated, header row required</div>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
              />
            </div>

            {preview && preview.headers?.length > 0 && (() => {
              const known = KNOWN_COLUMNS[resource] || new Set();
              const unknownIdx = preview.headers
                .map((h, i) => (h && !known.has(normalizeHeader(h))) ? i : -1)
                .filter((i) => i >= 0);
              return (
              <div className="border border-border rounded-md bg-secondary/30 overflow-hidden" data-testid="bulk-csv-preview">
                <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Preview · first {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {unknownIdx.length > 0 && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-medium dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/50"
                        data-testid="bulk-csv-unknown-badge"
                      >
                        {unknownIdx.length} unknown column{unknownIdx.length === 1 ? "" : "s"} — will be ignored on import
                      </span>
                    )}
                    {preview.truncated && (
                      <span className="text-[10px] text-muted-foreground">More rows in file</span>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto max-w-full">
                  <table className="w-full text-[11px]">
                    <thead className="bg-secondary/50">
                      <tr>
                        {preview.headers.map((h, i) => {
                          const isUnknown = h && !known.has(normalizeHeader(h));
                          return (
                            <th
                              key={i}
                              className={
                                "text-left px-3 py-1.5 font-semibold whitespace-nowrap border-b border-border/60 " +
                                (isUnknown
                                  ? "text-rose-700 bg-rose-50/70 dark:bg-rose-950/40 dark:text-rose-300"
                                  : "text-muted-foreground")
                              }
                              title={isUnknown ? "Unknown column — the importer will ignore this on upload" : undefined}
                              data-testid={isUnknown ? `bulk-csv-header-unknown-${i}` : undefined}
                            >
                              {h || <span className="italic text-destructive">(blank)</span>}
                              {isUnknown && <span className="ml-1 text-rose-500">•</span>}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, ri) => (
                        <tr key={ri} className="odd:bg-background/60">
                          {preview.headers.map((_, ci) => (
                            <td
                              key={ci}
                              className={
                                "px-3 py-1.5 whitespace-nowrap border-b border-border/40 max-w-[220px] overflow-hidden text-ellipsis " +
                                (unknownIdx.includes(ci) ? "text-rose-700/80 dark:text-rose-300/80" : "")
                              }
                            >
                              {r[ci] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}
            {preview?.error && (
              <div className="text-xs text-destructive" data-testid="bulk-csv-preview-error">{preview.error}</div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                type="submit"
                disabled={busy || !file || (isSuper && !effectiveTenantId)}
                data-testid="bulk-submit-button"
                onClick={() => setDryRun(false)}
              >
                {busy && !dryRun ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Import {RESOURCES.find((r) => r.value === resource)?.label}
              </Button>
              <Button
                type="submit"
                variant="secondary"
                disabled={busy || !file || (isSuper && !effectiveTenantId)}
                data-testid="bulk-dryrun-button"
                onClick={() => setDryRun(true)}
                title="Validate every row without writing to the database"
              >
                {busy && dryRun ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                Dry run
              </Button>
              <Button type="button" variant="outline" onClick={downloadSample} data-testid="bulk-download-sample">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Sample CSV
              </Button>
            </div>
          </form>

          {/* Sample preview */}
          <div className="border border-border rounded-md bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-primary" />
              <div className="font-display font-bold text-sm">Schema preview</div>
            </div>
            <pre className="font-mono text-[10px] leading-relaxed bg-secondary/70 rounded p-3 overflow-auto max-h-72" data-testid="sample-csv-preview">
              {sample || "—"}
            </pre>
          </div>
        </div>

        {summary && (
          <div className="mt-8 border border-border rounded-md bg-card" data-testid="bulk-summary">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground flex items-center gap-2">
                  {summary.dry_run ? (
                    <>
                      <Eye className="h-3 w-3" /> Dry run report · {summary.resource}
                    </>
                  ) : (
                    <>Import report · {summary.resource}</>
                  )}
                </div>
                <h3 className="font-display font-bold text-lg mt-0.5">
                  <span className="text-[hsl(var(--success))]">{summary.inserted}</span>
                  <span className="text-muted-foreground"> / {summary.total} rows {summary.dry_run ? "would import" : "imported"}</span>
                </h3>
                {summary.dry_run && (
                  <div className="text-xs text-amber-700 dark:text-amber-500 mt-1">
                    No data was written. Click <span className="font-semibold">Import</span> to commit.
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {hasCredentials && !summary.dry_run && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadCredentials}
                    data-testid="bulk-download-credentials"
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800/60 dark:hover:bg-emerald-950/40"
                  >
                    <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Download credentials
                  </Button>
                )}
                <Badge className="bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2]">
                  {summary.inserted} OK
                </Badge>
                {summary.failed > 0 && (
                  <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                    {summary.failed} failed
                  </Badge>
                )}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-20">Status</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.results.map((r) => (
                  <TableRow key={r.row} data-testid={`bulk-result-row-${r.row}`}>
                    <TableCell className="font-mono text-xs">{r.row}</TableCell>
                    <TableCell>
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]">
                          <CheckCircle2 className="h-3.5 w-3.5" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive">
                          <XCircle className="h-3.5 w-3.5" /> Fail
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.ok ? (
                        <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
                      ) : (
                        <span className="text-destructive">{r.error}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
